<script setup lang="ts">
import type { LocationQuery, RouteParams } from 'vue-router'
import { CtIcon } from '@camelot/components'
import { checkIsWebView, getSafeAreaTopInset, refreshAuthToken } from '@camelot/webview-utils'
import { ref } from 'vue'
import { useConsentManagement } from '~/composables/tracking/consent-management'
import { useDataLayer } from '~/composables/tracking/datalayer'
import { useSentry } from '~/composables/tracking/sentry'
import { loadTranslations } from '~/composables/translate/translations'

const { $initDeviceWebview } = useNuxtApp()

const languageLoaded = ref(false)
const { pushPendingEvents } = useDataLayer()

const isWebview = computed<boolean>(() =>
  checkIsWebView(window),
)

const isEmbeddedInIframe = computed<boolean>(() =>
  window.location !== window.parent.location,
)

async function handleLanguageLoading(params: RouteParams, query: LocationQuery) {
  const lang = params._lang?.toString() || query.locale as string
  languageLoaded.value = await loadTranslations(lang)
}

function initializeTracking(initialLegalEntity: number = 0) {
  // init Consent Manager
  const { isSentryConsentGiven, isGTMConsentGiven } = useConsentManagement(initialLegalEntity)
  if (isGTMConsentGiven)
    pushPendingEvents()

  useSentry(isSentryConsentGiven)
}

function initializeWebview() {
  if (isWebview.value) {
    document.body.style.paddingTop = `${getSafeAreaTopInset(window)}px`
    document.body.classList.add('webview')
    $initDeviceWebview()
    if (useAppStore().accessToken === '') {
      refreshAuthToken(window)
    }
  }
}

onBeforeMount(() => {
  const { params, query } = useRoute()
  if (!isEmbeddedInIframe.value)
    initializeTracking()

  // For Referral, the language is defined as a query parameter instead of a path parameter
  handleLanguageLoading(params, query)
  initializeWebview()
})

onMounted(() => {
})
</script>

<template>
  <div v-if="languageLoaded">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
  <div v-else class="flex justify-center items-center">
    <CtIcon name="loading_web" class="animate-spin w-full h-full basis-1/12" color="transparent" />
  </div>
</template>
